import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'

import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
  DateTimePicker,
  Flex,
  formatPlainTemporal,
  IconButton,
  InfoIcon,
  Label,
  Popover,
  Tooltip,
} from '@buffer-mono/popcorn'

import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import { useTwentyFourHourTimeFormat } from '~publish/hooks/useTwentyFourHourTimeFormat'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'

import { useTemplateLibrary } from '../TemplateLibraryContext'

import styles from './InternalPreviewDatePicker.module.css'

export const InternalPreviewDatePicker = ({
  variant = 'default',
}: {
  variant?: 'default' | 'compact'
}): JSX.Element | null => {
  const twentyFourHourTimeFormat = useTwentyFourHourTimeFormat()
  const weekStartsOn = useWeekStartsOn()
  const [isOpen, setIsOpen] = useState(false)

  const { currentTime, isPreviewSet, setPreviewDate, adjustPreviewByDays } =
    useTemplateLibrary()

  const handleDateChange = (date?: Temporal.ZonedDateTime): void => {
    setPreviewDate(date)
    setIsOpen(false)
  }

  const formattedTime = useMemo(() => {
    if (variant === 'compact') {
      return formatPlainTemporal(currentTime.toPlainDate(), 'en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      })
    }
    return `Preview: ${formatPlainTemporal(currentTime.toPlainDate(), 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`
  }, [currentTime, variant])

  return (
    <Flex
      align="center"
      gap={isPreviewSet ? '3xs' : undefined}
      className={clsx(styles.container, isPreviewSet && styles.selected)}
    >
      <Tooltip
        content="Internal only: Preview how templates appear on different campaign days"
        side="bottom"
      >
        <IconButton
          label="Preview date info"
          size="small"
          variant="tertiary"
          className={styles.iconButton}
        >
          <InfoIcon color="brand" />
        </IconButton>
      </Tooltip>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        {isPreviewSet ? (
          <>
            <IconButton
              label="Previous day"
              size="small"
              variant="tertiary"
              className={styles.iconButton}
              onClick={() => adjustPreviewByDays(-1)}
            >
              <ChevronLeftIcon color="brand" />
            </IconButton>
            <IconButton
              label="Next day"
              size="small"
              variant="tertiary"
              className={styles.iconButton}
              onClick={() => adjustPreviewByDays(1)}
            >
              <ChevronRightIcon color="brand" />
            </IconButton>
            <Popover.Trigger>
              <Button
                size="small"
                variant="tertiary"
                className={styles.dateButton}
              >
                {formattedTime}
              </Button>
            </Popover.Trigger>
            <IconButton
              label="Clear preview date"
              size="small"
              variant="tertiary"
              className={clsx(styles.iconButton, styles.clearButton)}
              onClick={() => setPreviewDate(undefined)}
            >
              <XIcon color="brand" />
            </IconButton>
          </>
        ) : (
          <Popover.Trigger>
            <Button
              size="small"
              variant="tertiary"
              className={styles.dateButton}
            >
              Set Preview Date
            </Button>
          </Popover.Trigger>
        )}
        <Popover.Content size="medium" className={styles.popover}>
          <DateTimePicker
            value={currentTime}
            onValueChange={handleDateChange}
            timezone={CURRENT_TIME_ZONE}
            min={Temporal.Now.zonedDateTimeISO(CURRENT_TIME_ZONE).subtract({
              months: 12,
            })}
            weekStartsOn={weekStartsOn === 'monday' ? 1 : 0}
            hourCycle={twentyFourHourTimeFormat ? 24 : 12}
            required={true}
          >
            <DateTimePicker.Calendar />
            <DateTimePicker.Separator />
            <Flex direction="column" gap="2xs" fullWidth>
              <Label
                htmlFor="themed-prompt-datetime-override-selector"
                size="small"
              >
                Select Time
              </Label>
              <DateTimePicker.TimeInput id="themed-prompt-datetime-override-selector" />
            </Flex>
          </DateTimePicker>
        </Popover.Content>
      </Popover>
    </Flex>
  )
}
